import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import WhatsAppButton from "@/components/WhatsAppButton";
import Home from "@/pages/Home";
import FAQ from "@/pages/FAQ";
import ReturnPolicy from "@/pages/ReturnPolicy";
import Compliance from "@/pages/Compliance";
import DoshaQuiz from "@/pages/DoshaQuiz";
import Practitioners from "@/pages/Practitioners";
import PractitionerDetail from "@/pages/PractitionerDetail";
import PractitionerJoin from "@/pages/PractitionerJoin";
import PractitionerAgreement from "@/pages/PractitionerAgreement";
import PractitionerDashboard from "@/pages/PractitionerDashboard";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Checkout from "@/pages/Checkout";
import { Login, Signup } from "@/pages/Auth";
import { Blog, BlogDetail } from "@/pages/Blog";
import VendorRegister from "@/pages/VendorRegister";
import VendorAgreement from "@/pages/VendorAgreement";
import VendorDashboard from "@/pages/VendorDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import CustomerAccount from "@/pages/CustomerAccount";
import AuthCallback from "@/pages/AuthCallback";

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

function Shell() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return (
      <div className="min-h-screen flex flex-col"><Header /><main className="flex-1"><AuthCallback /></main></div>
    );
  }
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <CartDrawer />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogDetail />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/return-policy" element={<ReturnPolicy />} />
          <Route path="/compliance" element={<Compliance />} />
          <Route path="/dosha-quiz" element={<DoshaQuiz />} />
          <Route path="/practitioners" element={<Practitioners />} />
          <Route path="/practitioner/:id" element={<PractitionerDetail />} />
          <Route path="/practitioner/join" element={<PractitionerJoin />} />
          <Route path="/practitioner/agreement" element={<PractitionerAgreement />} />
          <Route path="/practitioner/dashboard" element={<Protected role="practitioner"><PractitionerDashboard /></Protected>} />
          <Route path="/vendor/register" element={<VendorRegister />} />
          <Route path="/vendor/agreement" element={<VendorAgreement />} />
          <Route path="/vendor" element={<Protected role="vendor"><VendorDashboard /></Protected>} />
          <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
          <Route path="/account" element={<Protected><CustomerAccount /></Protected>} />
        </Routes>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <CartProvider>
          <Shell />
          <Toaster position="top-right" richColors />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
